/**
 * Pokemon Showdown team text import/export parser.
 * Parses the standard text format used by Pokemon Showdown's team builder.
 */

import type { PokemonSet } from "../engine/types";

/**
 * Parse a Showdown text format team into an array of PokemonSet.
 * Each Pokemon is separated by a blank line. Lines:
 *   Species (Gender) @ Item
 *   Ability: Name
 *   Nature: Name
 *   EVs: HP x / Atk x / ...
 *   IVs: HP x / Atk x / ...
 *   - MoveName
 */
export function parseShowdownText(text: string): PokemonSet[] {
  const blocks = text.trim().split(/\n\s*\n/);
  const sets: PokemonSet[] = [];

  for (const block of blocks) {
    const lines = block.split("\n").map((l) => l.trim());
    if (lines.length === 0 || !lines[0]) continue;

    const set = parsePokemonBlock(lines);
    if (set) sets.push(set);
  }

  return sets;
}

function parsePokemonBlock(lines: string[]): PokemonSet | null {
  const firstLine = lines[0];
  if (!firstLine) return null;

  // Parse species, gender, item from first line
  // Format: "Species (Gender) @ Item" or "Species @ Item" or "Species (Gender)" or "Species"
  let species = "";
  let gender: "" | "M" | "F" = "";
  let item: string | undefined;

  const atSplit = firstLine.split(" @ ");
  const speciesPart = atSplit[0].trim();
  if (atSplit[1]) item = atSplit[1].trim();

  const genderMatch = speciesPart.match(/\((M|F|N)\)$/);
  if (genderMatch) {
    species = speciesPart.slice(0, genderMatch.index).trim();
    if (genderMatch[1] === "M") gender = "M";
    else if (genderMatch[1] === "F") gender = "F";
    else gender = "";
  } else {
    species = speciesPart;
  }

  // Handle nicknames: "Nickname (Species)"
  const nickMatch = species.match(/^(.+?) \((.+)\)$/);
  let name: string | undefined;
  if (nickMatch) {
    name = nickMatch[1];
    species = nickMatch[2];
  }

  const set: PokemonSet = {
    species,
    ability: "",
    moves: [],
  };
  if (name) set.name = name;
  if (gender) set.gender = gender;
  if (item) set.item = item;

  let level = 100;
  let happiness = 255;

  for (let i = 1; i < lines.length; i++) {
    const line = lines[i];
    if (line.startsWith("Ability: ")) {
      set.ability = line.slice(9).trim();
    } else if (line.startsWith("Level: ")) {
      const lv = parseInt(line.slice(7).trim(), 10);
      if (!Number.isNaN(lv)) level = lv;
    } else if (line.startsWith("Happiness: ")) {
      const h = parseInt(line.slice(11).trim(), 10);
      if (!Number.isNaN(h)) happiness = h;
    } else if (line.startsWith("Nature: ")) {
      set.nature = line.slice(8).trim();
    } else if (line.startsWith("EVs: ")) {
      set.evs = parseStatLine(line.slice(4));
    } else if (line.startsWith("IVs: ")) {
      set.ivs = parseStatLine(line.slice(4));
    } else if (line.startsWith("Shiny: Yes")) {
      set.shiny = true;
    } else if (line.startsWith("- ")) {
      set.moves.push(line.slice(2).trim());
    }
  }

  set.level = level;
  set.happiness = happiness;
  return set;
}

function parseStatLine(text: string): Record<string, number> {
  const stats: Record<string, number> = {};
  const parts = text.trim().split("/");
  for (const part of parts) {
    const trimmed = part.trim();
    const spaceIdx = trimmed.indexOf(" ");
    if (spaceIdx === -1) continue;
    const value = parseInt(trimmed.slice(0, spaceIdx), 10);
    const statName = trimmed.slice(spaceIdx + 1).trim().toLowerCase();
    if (!Number.isNaN(value)) {
      stats[statName] = value;
    }
  }
  return stats;
}

/**
 * Export an array of PokemonSet to Showdown text format.
 */
export function exportShowdownText(sets: PokemonSet[]): string {
  return sets.map(exportPokemonSet).join("\n\n");
}

function exportPokemonSet(set: PokemonSet): string {
  const lines: string[] = [];

  // First line: Nickname (Species) (Gender) @ Item
  let firstLine = "";
  if (set.name && set.name !== set.species) {
    firstLine = `${set.name} (${set.species})`;
  } else {
    firstLine = set.species;
  }
  if (set.gender) firstLine += ` (${set.gender})`;
  if (set.item) firstLine += ` @ ${set.item}`;
  lines.push(firstLine);

  if (set.ability) lines.push(`Ability: ${set.ability}`);
  if (set.level !== undefined && set.level !== 100)
    lines.push(`Level: ${set.level}`);
  if (set.happiness !== undefined && set.happiness !== 255)
    lines.push(`Happiness: ${set.happiness}`);
  if (set.nature) lines.push(`Nature: ${set.nature}`);
  if (set.shiny) lines.push("Shiny: Yes");

  if (set.evs) {
    const evStr = formatStatLine(set.evs);
    if (evStr) lines.push(`EVs: ${evStr}`);
  }
  if (set.ivs) {
    const ivStr = formatStatLine(set.ivs);
    if (ivStr) lines.push(`IVs: ${ivStr}`);
  }

  for (const move of set.moves) {
    lines.push(`- ${move}`);
  }

  return lines.join("\n");
}

function formatStatLine(stats: Record<string, number>): string {
  const statLabels: Record<string, string> = {
    hp: "HP",
    atk: "Atk",
    def: "Def",
    spa: "SpA",
    spd: "SpD",
    spe: "Spe",
  };
  const parts: string[] = [];
  for (const [stat, label] of Object.entries(statLabels)) {
    const val = stats[stat];
    if (val !== undefined) {
      parts.push(`${val} ${label}`);
    }
  }
  return parts.join(" / ");
}
